import { useState, useEffect,useCallback,createContext,useContext, useRef } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { useNavigate } from "react-router-dom";

// ─────────────────────────────────────────────
// COLOR_MAP  (used by Shop.jsx)
// ─────────────────────────────────────────────
export const COLOR_MAP = {
  black:  "#1c1c1c", white:  "#f5f5f5", red:    "#e53e3e", rose:   "#fb7185",
  pink:   "#f472b6", blue:   "#3b82f6", navy:   "#1e3a5f", green:  "#22c55e",
  olive:  "#6b7c45", yellow: "#facc15", gold:   "#d97706", beige:  "#e8d5b7",
  cream:  "#fdf6e3", gray:   "#9ca3af", brown:  "#92400e", purple: "#a855f7",
};

// ─────────────────────────────────────────────
// Toast context
// ─────────────────────────────────────────────
const ToastContext = createContext(null);

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  }, []);
  return (
    <ToastContext.Provider value={{ addToast }}>
      {children}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[9999] flex flex-col gap-2 items-center pointer-events-none">
        {toasts.map(({ id, message, type }) => (
          <div key={id} className={`px-5 py-3 rounded-full text-sm font-medium shadow-lg text-white ${type === "error" ? "bg-red-500" : "bg-stone-900"}`}>
            {message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

// ─────────────────────────────────────────────
// Auth context
// ─────────────────────────────────────────────
const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/auth`;

function getStoredAuth() {
  try {
    const token = localStorage.getItem("sf_token");
    const user  = JSON.parse(localStorage.getItem("sf_user") || "null");
    return token && user ? { token, user } : { token: null, user: null };
  } catch { return { token: null, user: null }; }
}

export function AuthProvider({ children }) {
  const { token: initToken, user: initUser } = getStoredAuth();
  const [user,  setUser]  = useState(initUser);
  const [token, setToken] = useState(initToken);
  const [authModalOpen,     setAuthModalOpen]     = useState(false);
  const [authModalCallback, setAuthModalCallback] = useState(null);

  const persist = useCallback((t, u) => {
    if (t && u) {
      localStorage.setItem("sf_token", t);
      localStorage.setItem("sf_user", JSON.stringify(u));
    } else {
      localStorage.removeItem("sf_token");
      localStorage.removeItem("sf_user");
    }
    setToken(t); setUser(u);
    window.dispatchEvent(new Event("sf:auth-change"));
  }, []);

  const login  = useCallback((t, u) => persist(t, u), [persist]);

  // ── Updated logout — clears admin tokens too ──
  const logout = useCallback(() => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminUser");
    persist(null, null);
  }, [persist]);

  const requireAuth = useCallback((cb) => {
    if (user) { cb(); return; }
    setAuthModalCallback(() => cb);
    setAuthModalOpen(true);
  }, [user]);

  useEffect(() => {
    const handler = (e) => requireAuth(e.detail?.onSuccess ?? (() => {}));
    window.addEventListener("sf:require-auth", handler);
    return () => window.removeEventListener("sf:require-auth", handler);
  }, [requireAuth]);

  const openAuthModal  = useCallback(() => setAuthModalOpen(true), []);
  const closeAuthModal = useCallback(() => { setAuthModalOpen(false); setAuthModalCallback(null); }, []);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, requireAuth, openAuthModal, closeAuthModal, isLoggedIn: !!user }}>
      {children}
      {authModalOpen && (
        <AuthModal
          onClose={closeAuthModal}
          onSuccess={(t, u) => { login(t, u); closeAuthModal(); if (authModalCallback) authModalCallback(); }}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

// ─────────────────────────────────────────────
// Auth API helper
// ─────────────────────────────────────────────
async function apiPost(path, body) {
  const res  = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || "Something went wrong.");
  return data;
}

// ─────────────────────────────────────────────
// AuthModal
// ─────────────────────────────────────────────
function EyeIcon({ open }) {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
      {open ? (
        <><path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></>
      ) : (
        <><path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></>
      )}
    </svg>
  );
}

function AuthModal({ onClose, onSuccess }) {
  const [view,       setView]       = useState("login");
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [resetToken, setResetToken] = useState("");
  const [name,       setName]       = useState("");
  const [email,      setEmail]      = useState("");
  const [password,   setPassword]   = useState("");
  const [confirm,    setConfirm]    = useState("");
  const [otp,        setOtp]        = useState("");
  const [newPwd,     setNewPwd]     = useState("");
  const [showPass,   setShowPass]   = useState(false);

  const clr = () => setError("");

  // ── Updated handleLogin — syncs admin tokens ──
  async function handleLogin(e) {
    e.preventDefault(); clr(); setLoading(true);
    try {
      const d = await apiPost("/login", { email, password });
      // If admin or store_manager, also set admin tokens
      if (d.user.role === "admin" || d.user.role === "store_manager") {
        localStorage.setItem("adminToken", d.token);
        localStorage.setItem("adminUser", JSON.stringify(d.user));
      }
      onSuccess(d.token, d.user);
    }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleRegister(e) {
    e.preventDefault(); clr();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try { const d = await apiPost("/register", { name, email, password }); onSuccess(d.token, d.user); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleSendOTP(e) {
    e.preventDefault(); clr(); setLoading(true);
    try { await apiPost("/send-otp", { email }); setView("otp"); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleVerifyOTP(e) {
    e.preventDefault(); clr(); setLoading(true);
    try { const d = await apiPost("/verify-otp", { email, otp }); setResetToken(d.resetToken); setView("reset"); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  async function handleReset(e) {
    e.preventDefault(); clr();
    if (newPwd !== confirm) { setError("Passwords do not match."); return; }
    setLoading(true);
    try { await apiPost("/reset-password", { resetToken, newPassword: newPwd }); setView("login"); setPassword(""); setConfirm(""); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }

  const inp = "w-full px-4 py-3 rounded-xl border border-stone-200 text-sm text-stone-800 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent transition";
  const btn = "w-full py-3.5 rounded-full text-sm font-semibold bg-stone-900 text-white hover:bg-rose-500 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed";

  const meta = {
    login:    { h: "Welcome back",      sub: "Sign in to your account" },
    register: { h: "Create account",    sub: "Join Sagar Fashion today" },
    forgot:   { h: "Forgot password?",  sub: "We'll email you an OTP" },
    otp:      { h: "Enter OTP",         sub: `Sent to ${email}` },
    reset:    { h: "Set new password",  sub: "Choose a strong password" },
  };

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="pointer-events-auto bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 relative" onClick={e => e.stopPropagation()}>

          <button onClick={onClose} className="absolute top-5 right-5 w-8 h-8 rounded-full bg-stone-100 flex items-center justify-center text-stone-500 hover:bg-stone-200 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="flex justify-center mb-5">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-rose-400 to-amber-500 flex items-center justify-center shadow-md">
              <span className="text-white text-sm font-bold">SF</span>
            </div>
          </div>

          <h2 className="serif text-2xl font-bold text-stone-900 text-center mb-1">{meta[view].h}</h2>
          <p className="text-sm text-stone-400 text-center mb-6">{meta[view].sub}</p>

          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600">{error}</div>
          )}

          {view === "login" && (
            <form onSubmit={handleLogin} className="space-y-4">
              <input type="email" required placeholder="Email address" value={email} onChange={e => setEmail(e.target.value)} className={inp} />
              <div className="relative">
                <input type={showPass ? "text" : "password"} required placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} className={inp + " pr-11"} />
                <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"><EyeIcon open={showPass} /></button>
              </div>
              <div className="text-right">
                <button type="button" onClick={() => { setView("forgot"); clr(); }} className="text-xs text-rose-500 hover:underline">Forgot password?</button>
              </div>
              <button type="submit" disabled={loading} className={btn}>{loading ? "Signing in…" : "Sign In"}</button>
            </form>
          )}

          {view === "register" && (
            <form onSubmit={handleRegister} className="space-y-4">
              <input type="text"  required placeholder="Full name"               value={name}     onChange={e => setName(e.target.value)}     className={inp} />
              <input type="email" required placeholder="Email address"           value={email}    onChange={e => setEmail(e.target.value)}    className={inp} />
              <div className="relative">
                <input type={showPass ? "text" : "password"} required placeholder="Password (min 6 chars)" value={password} onChange={e => setPassword(e.target.value)} className={inp + " pr-11"} />
                <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"><EyeIcon open={showPass} /></button>
              </div>
              <input type="password" required placeholder="Confirm password" value={confirm} onChange={e => setConfirm(e.target.value)} className={inp} />
              <button type="submit" disabled={loading} className={btn}>{loading ? "Creating account…" : "Create Account"}</button>
            </form>
          )}

          {view === "forgot" && (
            <form onSubmit={handleSendOTP} className="space-y-4">
              <input type="email" required placeholder="Your registered email" value={email} onChange={e => setEmail(e.target.value)} className={inp} />
              <button type="submit" disabled={loading} className={btn}>{loading ? "Sending OTP…" : "Send OTP"}</button>
            </form>
          )}

          {view === "otp" && (
            <form onSubmit={handleVerifyOTP} className="space-y-4">
              <p className="text-sm text-stone-500 text-center">Check your inbox and enter the 6-digit code.</p>
              <input
                type="text" required placeholder="000000" maxLength={6}
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))}
                className={inp + " text-center text-2xl tracking-[0.5em] font-bold"}
              />
              <button type="submit" disabled={loading || otp.length !== 6} className={btn}>{loading ? "Verifying…" : "Verify OTP"}</button>
              <button type="button" onClick={handleSendOTP} className="w-full text-xs text-stone-400 hover:text-rose-500 transition-colors">
                Didn't receive it? Resend OTP
              </button>
            </form>
          )}

          {view === "reset" && (
            <form onSubmit={handleReset} className="space-y-4">
              <input type="password" required placeholder="New password (min 6 chars)" value={newPwd}   onChange={e => setNewPwd(e.target.value)}   className={inp} />
              <input type="password" required placeholder="Confirm new password"       value={confirm}  onChange={e => setConfirm(e.target.value)}  className={inp} />
              <button type="submit" disabled={loading} className={btn}>{loading ? "Updating…" : "Update Password"}</button>
            </form>
          )}

          {(view === "login" || view === "register") && (
            <p className="text-center text-sm text-stone-400 mt-6">
              {view === "login" ? "Don't have an account? " : "Already have an account? "}
              <button onClick={() => { setView(view === "login" ? "register" : "login"); clr(); }} className="text-rose-500 font-semibold hover:underline">
                {view === "login" ? "Register" : "Sign In"}
              </button>
            </p>
          )}

          {(view === "forgot" || view === "otp" || view === "reset") && (
            <p className="text-center text-sm text-stone-400 mt-6">
              <button onClick={() => { setView("login"); clr(); }} className="text-rose-500 hover:underline">← Back to Sign In</button>
            </p>
          )}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// AuthButton
// ─────────────────────────────────────────────
function AuthButton() {
  const { user, isLoggedIn, openAuthModal, logout } = useAuth();
  const { addToast } = useToast();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  if (!isLoggedIn) {
    return (
      <button onClick={openAuthModal}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-stone-200 text-stone-700 text-sm font-semibold hover:border-rose-400 hover:text-rose-500 transition-all duration-300">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        <span className="hidden sm:inline">Login</span>
      </button>
    );
  }

  const firstName = user.name?.split(" ")[0] ?? "Account";

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-stone-100 text-stone-800 text-sm font-semibold hover:bg-rose-50 hover:text-rose-500 transition-all duration-300">
        <span className="w-6 h-6 rounded-full bg-gradient-to-br from-rose-400 to-amber-500 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
          {firstName[0].toUpperCase()}
        </span>
        <span className="hidden sm:inline max-w-[100px] truncate">{firstName}</span>
        <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-stone-100 rounded-2xl shadow-xl overflow-hidden z-50">
          {/* User info */}
          <div className="px-4 py-3 border-b border-stone-100 bg-stone-50">
            <p className="text-sm font-semibold text-stone-800 truncate">{user.name}</p>
            <p className="text-xs text-stone-400 truncate">{user.email}</p>
            {(user.role === "admin" || user.role === "store_manager") && (
              <span className="text-xs font-semibold text-rose-500 capitalize">{user.role.replace("_", " ")}</span>
            )}
          </div>

          {/* Admin Panel — only for admin/store_manager */}
          {(user.role === "admin" || user.role === "store_manager") && (
            <Link to="/admin/dashboard" onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-4 py-3 text-sm text-rose-600 hover:bg-rose-50 transition-colors font-semibold border-b border-stone-100">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              Admin Panel
            </Link>
          )}

          {/* Settings */}
          <Link to="/settings" onClick={() => setOpen(false)}
            className="flex items-center gap-3 px-4 py-3 text-sm text-stone-700 hover:bg-rose-50 hover:text-rose-500 transition-colors">
            <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Settings
          </Link>

          {/* Logout */}
          <button
            onClick={() => { logout(); setOpen(false); addToast("Logged out successfully"); }}
            className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors border-t border-stone-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// QtyControl
// ─────────────────────────────────────────────
export function QtyControl({ value, onChange, min = 1, max = 99, variant = "light" }) {
  const isDark = variant === "dark";

  const containerClasses = isDark
    ? "flex items-center gap-1 border border-white/30 rounded-full px-1 py-0.5"
    : "flex items-center gap-1 border border-stone-200 rounded-full px-1 py-0.5";

  const buttonClasses = isDark
    ? "w-7 h-7 rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors text-lg leading-none"
    : "w-7 h-7 rounded-full flex items-center justify-center text-stone-600 hover:bg-stone-100 transition-colors text-lg leading-none";

  const numberClasses = isDark
    ? "w-6 text-center text-sm font-semibold text-white"
    : "w-6 text-center text-sm font-semibold text-stone-800";

  return (
    <div className={containerClasses}>
      <button onClick={() => onChange(Math.max(min, value - 1))} className={buttonClasses}>−</button>
      <span className={numberClasses}>{value}</span>
      <button onClick={() => onChange(Math.min(max, value + 1))} className={buttonClasses}>+</button>
    </div>
  );
}

// ─────────────────────────────────────────────
// CartDrawer
// ─────────────────────────────────────────────
export function CartDrawer({ open, onClose }) {
  const cart = useCart() || {};
  const { items = [], removeItem, updateQty, clearCart, totalPrice = 0,  } = cart;
  const { addToast }              = useToast();
  const { requireAuth, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  function handleCheckout() {
    requireAuth(() => {
      onClose();
      navigate("/checkout");
    });
  }

  return (
    <>
      <div onClick={onClose} className={`fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} />
      <div className={`fixed top-0 right-0 h-full w-full max-w-sm z-50 bg-white shadow-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`}>

        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
          <h2 className="serif text-xl font-semibold text-stone-900">Your Cart</h2>
          <button onClick={onClose} className="w-9 h-9 rounded-full bg-stone-100 flex items-center justify-center text-stone-600 hover:bg-stone-200 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-stone-400">
              <svg className="w-16 h-16 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <p className="text-sm">Your cart is empty</p>
            </div>
          ) : items.map(item => {
            const pid = item.product_id || item.id;
            return (
              <div key={`${pid}-${item.selectedColor || ""}-${item.selectedSize || ""}`} className="flex gap-4 items-start">
                {(item.img || item.image) && (
                  <img src={item.img ?? item.image} alt={item.name} className="w-16 h-16 object-cover rounded-xl flex-shrink-0 bg-stone-100" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-stone-800 truncate">{item.name}</p>
                  {item.selectedColor && <p className="text-xs text-stone-400 capitalize">{item.selectedColor}</p>}
                  {item.selectedSize  && <p className="text-xs text-stone-400">Size: {item.selectedSize}</p>}
                  <div className="flex items-center justify-between mt-2">
                    <QtyControl
                      value={item.quantity || item.qty || 1}
                      onChange={qty => updateQty(pid, qty, item.selectedColor, item.selectedSize)}
                    />
                    <span className="text-sm font-bold text-stone-900">
                      ${((item.effective_price ?? item.sale_price ?? item.price) * (item.quantity || item.qty || 1)).toLocaleString("en-IN")}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => removeItem(pid, item.selectedColor, item.selectedSize)}
                  className="text-stone-300 hover:text-rose-400 transition-colors mt-0.5"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            );
          })}
        </div>

        {items.length > 0 && (
          <div className="px-6 py-5 border-t border-stone-100 space-y-3">
            <div className="flex justify-between text-sm text-stone-500">
              <span>Subtotal</span>
              <span className="font-semibold text-stone-900">${totalPrice.toLocaleString("en-IN")}</span>
            </div>
            {!isLoggedIn && (
              <p className="text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-xl">
                🔒 You'll be asked to sign in before checkout
              </p>
            )}
            <button onClick={handleCheckout}
              className="w-full bg-stone-900 text-white py-3.5 rounded-full text-sm font-semibold hover:bg-rose-500 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-rose-200">
              Checkout
            </button>
          </div>
        )}

        {/* ─── TRUST FOOTER STRIP ──────────────────────────────── */}
        <div className="border-t border-stone-100 px-6 py-3 flex items-center justify-between gap-2 text-[10px] text-stone-400 font-medium">
          <Link to="/returns" className="flex items-center gap-1 hover:text-rose-500 transition-colors">
            <span>↩️</span> Easy Returns
          </Link>
          <span className="flex items-center gap-1">
            <span>🔒</span> Secure Checkout
          </span>
          <a href="mailto:sagarfashion777@gmail.com" className="flex items-center gap-1 hover:text-rose-500 transition-colors">
            <span>📞</span> Support
          </a>
        </div>
        {/* ─── END TRUST FOOTER ──────────────────────────────────── */}
      </div>
    </>
  );
}

// ─────────────────────────────────────────────
// NavbarGlobalStyles
// ─────────────────────────────────────────────
export function NavbarGlobalStyles() {
  return (
    <style>{`
      @keyframes badge-pop {
        0%{transform:scale(1)}40%{transform:scale(1.5)}70%{transform:scale(0.9)}100%{transform:scale(1)}
      }
      @keyframes cart-wiggle {
        0%,100%{transform:rotate(0deg)}20%{transform:rotate(-12deg)}50%{transform:rotate(10deg)}75%{transform:rotate(-6deg)}
      }
      @keyframes pulse-ring {
        0%{transform:scale(0.8);opacity:0.8}100%{transform:scale(2.2);opacity:0}
      }
      .sf-badge-pop{animation:badge-pop 0.45s cubic-bezier(.36,.07,.19,.97) forwards}
      .sf-cart-wiggle{animation:cart-wiggle 0.55s ease-in-out}
    `}</style>
  );
}

// ─────────────────────────────────────────────
// Navbar
// ─────────────────────────────────────────────
export function Navbar({ onCartOpen, links, activeLink = "Shop" }) {
  const { totalItems }                          = useCart();
  const [scrolled,  setScrolled]  = useState(false);
  const [menuOpen,  setMenuOpen]  = useState(false);
  const [prevItems, setPrevItems] = useState(totalItems);
  const [badgeAnim, setBadgeAnim] = useState(false);
  const [cartAnim,  setCartAnim]  = useState(false);

  const navLinks = links ?? [
  { label: "Home",      href: "/" },
  { label: "Clothing",      href: "/shop" },
  { label: "Jewelry",   href: "/shop?category=Jewelry" },
  // { label: "Cosmetics", href: "/cosmetics" },
  { label: "Tutorials", href: "/tutorials" },
  { label: "Contact",   href: "/contact" },
];

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    if (totalItems > prevItems) {
      setBadgeAnim(true); setCartAnim(true);
      setTimeout(() => setBadgeAnim(false), 450);
      setTimeout(() => setCartAnim(false), 550);
    }
    setPrevItems(totalItems);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalItems]);

  useEffect(() => { setMenuOpen(false); }, [activeLink]);

  return (
    <nav className={`fixed top-0 left-0 right-0 z-30 transition-all duration-400 ${scrolled ? "bg-white/96 backdrop-blur-md shadow-sm" : "bg-white border-b border-stone-100"}`}>
      <div className="max-w-screen-xl mx-auto px-6 flex items-center justify-between py-4">

        <a href="/" className="flex flex-col items-center leading-none group">
  <span className="text-stone-900 font-bold tracking-[0.15em] uppercase text-sm"
    style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "0.18em" }}>
    Sagar Lifestyle NY
  </span>
  <div className="w-full flex items-center gap-1 my-[3px]">
    <div className="flex-1 h-px bg-amber-600" />
    <span className="text-amber-600 text-[9px] font-bold tracking-[0.3em] uppercase">LLC</span>
    <div className="flex-1 h-px bg-amber-600" />
  </div>
</a>

        <div className="hidden md:flex items-center gap-7">
          {navLinks.map(({ label, href }) => (
            <Link key={label} to={href}
              className={`text-sm font-medium transition-colors hover:text-rose-500 relative after:absolute after:left-0 after:-bottom-0.5 after:h-px after:w-0 after:bg-rose-400 after:transition-all after:duration-300 hover:after:w-full ${label === activeLink ? "text-rose-500 after:w-full" : "text-stone-600"}`}>
              {label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <AuthButton />

          <button onClick={onCartOpen}
            className="relative flex items-center gap-2.5 bg-stone-900 text-white px-5 py-2.5 rounded-full hover:bg-rose-500 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-rose-200 group">
            <svg className={`w-4 h-4 flex-shrink-0 ${cartAnim ? "sf-cart-wiggle" : ""}`} fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <span className="text-sm font-semibold hidden sm:inline">Cart</span>
            {totalItems > 0 && (
              <span className={`w-5 h-5 bg-rose-500 group-hover:bg-white group-hover:text-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center transition-colors ${badgeAnim ? "sf-badge-pop" : ""}`}>
                {totalItems > 9 ? "9+" : totalItems}
              </span>
            )}
            {badgeAnim && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-400 opacity-70" style={{ animation: "pulse-ring 0.6s ease-out forwards" }} />}
          </button>

          <button onClick={() => setMenuOpen(o => !o)}
            className="md:hidden w-10 h-10 rounded-full bg-stone-100 flex items-center justify-center text-stone-700 hover:bg-stone-200 transition-colors" aria-label="Toggle menu">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              {menuOpen ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
            </svg>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden border-t border-stone-100 bg-white px-4 pb-4 pt-2 flex flex-col gap-1">
          {navLinks.map(({ label, href }) => (
            <Link key={label} to={href} onClick={() => setMenuOpen(false)}
              className={`flex items-center justify-between py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 ${label === activeLink ? "bg-rose-50 text-rose-500" : "text-stone-700 hover:bg-stone-50 hover:text-rose-500"}`}>
              {label}
              {label === activeLink && <span className="w-1.5 h-1.5 rounded-full bg-rose-400" />}
            </Link>
          ))}
        </div>
      )}
    </nav>
  );
}

export default Navbar;